import { useEffect } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export interface FaqItem {
  question: string;
  answer: string;
}

interface FaqSectionProps {
  title?: string;
  items: FaqItem[];
  /** id sufixo para evitar colisão de <script> quando múltiplos FAQs coexistem */
  scriptId?: string;
}
const WHATSAPP_LINK = "https://wa.me/5521973548387";

const renderAnswer = (answer: string) => {
  const parts = answer.split(/(WhatsApp)/gi);
  return parts.map((part, i) =>
    part.toLowerCase() === "whatsapp" ? (
      <a
        key={i}
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="text-inherit no-underline hover:underline"
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
};

const FaqSection = ({ title = "Perguntas Frequentes", items, scriptId = "faq" }: FaqSectionProps) => {
  useEffect(() => {
    if (!items || items.length === 0) return;
    const id = `ld-faq-${scriptId}`;
    // remove existing to avoid duplicates on re-render
    document.getElementById(id)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, [items, scriptId]);

  if (!items || items.length === 0) return null;

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4 max-w-3xl">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
          {title}
        </h2>
        <Accordion type="single" collapsible className="w-full">
          {items.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="font-display text-left text-lg">
                {f.question}
              </AccordionTrigger>
              <AccordionContent className="font-body text-muted-foreground text-base">
                {renderAnswer(f.answer)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default FaqSection;
